import React, { useState, useEffect } from 'react';
import { Container, Grid, Box, Typography, Card, CardMedia, CardContent, IconButton, Modal, Link as MuiLink, Button } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { styled } from '@mui/material/styles';
import LoveIcon from '@mui/icons-material/Favorite'; // Icon for love button

// Import zodiac images
import ariesImage from '../images/zodiak/aries.png';
import taurusImage from '../images/zodiak/taurus.png';
import geminiImage from '../images/zodiak/gemini.png';
import cancerImage from '../images/zodiak/cancer.png';
import leoImage from '../images/zodiak/leo.png';
import virgoImage from '../images/zodiak/virgo.png';
import libraImage from '../images/zodiak/libra.png';
import scorpioImage from '../images/zodiak/scorpio.png';
import sagittariusImage from '../images/zodiak/sagitarius.png';
import capricornImage from '../images/zodiak/capricorn.png';
import aquariusImage from '../images/zodiak/aquarius.png';
import piscesImage from '../images/zodiak/pisces.png';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s ease',
  borderRadius: '8px',
  boxShadow: theme.shadows[3],
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledImage = styled(CardMedia)(({ theme }) => ({
  height: 120,
  width: 'auto',
  margin: '0 auto',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  color: theme.palette.common.white,
  padding: theme.spacing(8, 2),
  textAlign: 'center',
  height: '40vh', // Adjust height if needed
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#1976d2',
  marginBottom: theme.spacing(4), // Added margin bottom
  marginTop: theme.spacing(10), // Added margin top to create space from the navbar
  [theme.breakpoints.down('sm')]: {
    height: 'auto',
    padding: theme.spacing(6, 1),
    marginBottom: theme.spacing(2), // Adjust margin bottom for small screens
    marginTop: theme.spacing(6), // Adjust margin top for small screens
  },
}));

const zodiacSigns = [
  { name: 'Aries', image: ariesImage, link: 'https://en.wikipedia.org/wiki/Aries_(astrology)' },
  { name: 'Taurus', image: taurusImage, link: 'https://en.wikipedia.org/wiki/Taurus_(astrology)' },
  { name: 'Gemini', image: geminiImage, link: 'https://en.wikipedia.org/wiki/Gemini_(astrology)' },
  { name: 'Cancer', image: cancerImage, link: 'https://en.wikipedia.org/wiki/Cancer_(astrology)' },
  { name: 'Leo', image: leoImage, link: 'https://en.wikipedia.org/wiki/Leo_(astrology)' },
  { name: 'Virgo', image: virgoImage, link: 'https://en.wikipedia.org/wiki/Virgo_(astrology)' },
  { name: 'Libra', image: libraImage, link: 'https://en.wikipedia.org/wiki/Libra_(astrology)' },
  { name: 'Scorpio', image: scorpioImage, link: 'https://en.wikipedia.org/wiki/Scorpio_(astrology)' },
  { name: 'Sagittarius', image: sagittariusImage, link: 'https://en.wikipedia.org/wiki/Sagittarius_(astrology)' },
  { name: 'Capricorn', image: capricornImage, link: 'https://en.wikipedia.org/wiki/Capricorn_(astrology)' },
  { name: 'Aquarius', image: aquariusImage, link: 'https://en.wikipedia.org/wiki/Aquarius_(astrology)' },
  { name: 'Pisces', image: piscesImage, link: 'https://en.wikipedia.org/wiki/Pisces_(astrology)' },
];

const Home = () => {
  const [open, setOpen] = useState(false);
  const [currentSign, setCurrentSign] = useState(null);
  const [clickCounts, setClickCounts] = useState({}); // Track click counts

  useEffect(() => {
    const savedClickCounts = JSON.parse(localStorage.getItem('clickCounts')) || {};
    setClickCounts(savedClickCounts);
  }, []);

  useEffect(() => {
    localStorage.setItem('clickCounts', JSON.stringify(clickCounts));
  }, [clickCounts]);

  const handleOpen = (sign) => {
    setCurrentSign(sign);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleLoveClick = (sign) => {
    setClickCounts((prevCounts) => {
      const updatedCounts = {
        ...prevCounts,
        [sign.name]: (prevCounts[sign.name] || 0) + 1,
      };
      return updatedCounts;
    });
  };

  const handleScroll = () => {
    const scrollToTopButton = document.getElementById('scrollToTopButton');
    if (window.pageYOffset > 300) {
      scrollToTopButton.style.display = 'block';
    } else {
      scrollToTopButton.style.display = 'none';
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container maxWidth="lg">
      <HeroSection>
        <Typography 
          variant="body2" // Adjust font size to body2
          component="div" 
          gutterBottom 
          sx={{ 
            color: '#fff',
            fontSize: '0.875rem', // Smaller font size
            fontWeight: 'normal', // Normal font weight
            lineHeight: 1.4, // Adjust line height
            px: 2, // Add horizontal padding
          }}
        >
          <strong>Horoscopes by Zodiac ID</strong> <br />
          is a feature designed to provide in-depth insights into the 12 zodiac signs known in astrology. Each zodiac sign has unique characteristics, personality, and attributes that can give you an idea of how a person interacts with the world and the people around them.
          <br />
          On this page, you will find detailed information about each zodiac sign, including a representative image and a short description. Click on each zodiac card to read more about the characteristics, strengths, and challenges associated with that sign. You can also explore the link to Wikipedia for more in-depth information.
          <br />
          <strong>Horoscopes by Zodiac ID</strong> <br />
          not only offers knowledge about your zodiac sign but also allows you to interact with each sign through the 'Love' feature that allows you to show interest in certain signs. Use this feature to explore further and understand more about astrology and how zodiac signs can affect your life.
        </Typography>
      </HeroSection>

      <Grid container spacing={2} mt={4}>
        {zodiacSigns.map((sign) => (
          <Grid item xs={6} sm={4} md={2} key={sign.name}>
            <StyledCard>
              <StyledImage
                component="img"
                image={sign.image}
                alt={sign.name}
              />
              <CardContent>
                <Typography variant="h6" component="h2" align="center">
                  {sign.name}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                  <IconButton
                    aria-label={`Love ${sign.name}`}
                    onClick={() => handleLoveClick(sign)}
                    sx={{
                      color: clickCounts[sign.name] ? 'pink' : 'transparent',
                      backgroundColor: clickCounts[sign.name] ? 'rgba(255, 192, 203, 0.2)' : 'transparent',
                      '&:hover': {
                        backgroundColor: clickCounts[sign.name] ? 'rgba(255, 192, 203, 0.4)' : 'transparent',
                      },
                    }}
                  >
                    <LoveIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ ml: 1, mt: 1 }}>
                    {clickCounts[sign.name] || 0}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpen(sign)}
                    sx={{
                      ml: 1,
                      fontSize: '0.75rem',
                      padding: '4px 8px',
                      '&:hover': {
                        backgroundColor: '#115293',
                      },
                    }}
                  >
                    Read More
                  </Button>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
          <Typography variant="h6" component="h2">
            {currentSign?.name}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {currentSign?.name} details go here. You can add more information about the zodiac sign here.
          </Typography>
          <Box mt={2}>
            <MuiLink href={currentSign?.link} target="_blank" rel="noopener noreferrer" variant="body2">More details on Wikipedia</MuiLink>
          </Box>
        </Box>
      </Modal>

      <IconButton
        id="scrollToTopButton"
        onClick={handleScrollToTop}
        style={{
          display: 'none',
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          backgroundColor: '#1976d2',
          color: 'white',
          zIndex: 1000,
        }}
      >
        <ArrowUpwardIcon />
      </IconButton>
    </Container>
  );
};

export default Home;
